<template>
  <UiContainer class="profile-page">
    <div v-if="loading" class="profile-page__state">
      <UiProgress variant="spinner" />
      <p>Загрузка профиля...</p>
    </div>

    <div v-else class="profile-page__content">
      <div class="profile-page__header">
        <div>
          <h1 class="profile-page__title">Профиль</h1>
          <p class="profile-page__subtitle">Управление личными данными и настройками аккаунта</p>
        </div>
        <UiButton variant="secondary" @click="editMode = !editMode">
          {{ editMode ? 'Отменить' : 'Редактировать' }}
        </UiButton>
      </div>

      <div class="profile-page__grid">
        <div class="profile-page__col">
          <UiCard class="profile-page__card">
            <h2 class="profile-page__card-title">Личная информация</h2>
            <div class="profile-page__card-body">
              <div class="profile-page__fields profile-page__fields--two">
                <div class="profile-page__field">
                  <label class="profile-page__label">Имя</label>
                  <UiInput v-model="userForm.firstName" placeholder="Имя" :readonly="!editMode" />
                </div>
                <div class="profile-page__field">
                  <label class="profile-page__label">Фамилия</label>
                  <UiInput
                    v-model="userForm.lastName"
                    placeholder="Фамилия"
                    :readonly="!editMode"
                  />
                </div>
              </div>
              <div class="profile-page__field">
                <label class="profile-page__label">Email</label>
                <UiInput
                  v-model="userForm.email"
                  type="email"
                  placeholder="email@example.com"
                  :readonly="!editMode"
                />
              </div>
              <div class="profile-page__field">
                <label class="profile-page__label">Телефон</label>
                <UiInput
                  v-model="userForm.phone"
                  input-filter="phone"
                  placeholder="+7 …"
                  aria-label="Телефон"
                  :readonly="!editMode"
                />
              </div>
            </div>
          </UiCard>

          <UiCard class="profile-page__card">
            <h2 class="profile-page__card-title">Безопасность</h2>
            <div class="profile-page__card-body">
              <div class="profile-page__row">
                <div>
                  <span class="profile-page__row-title">Пароль</span>
                  <span class="profile-page__row-sub"
                    >Последнее изменение: {{ formatDate(user?.lastPasswordChange ?? null) }}</span
                  >
                </div>
                <UiButton variant="secondary" @click="showPasswordDialog = true">Изменить</UiButton>
              </div>
              <div class="profile-page__row">
                <div>
                  <span class="profile-page__row-title">Двухфакторная аутентификация</span>
                  <span class="profile-page__row-sub">
                    Подтверждение входа одноразовым кодом, который приходит на email
                  </span>
                </div>
                <UiSwitch
                  aria-label="Двухфакторная аутентификация"
                  :model-value="twoFactorEnabled"
                  :disabled="togglingTwoFactor"
                  @update:model-value="toggleTwoFactor"
                />
              </div>
            </div>
          </UiCard>
        </div>

        <div class="profile-page__col">
          <UiCard class="profile-page__card">
            <div class="profile-page__card-head">
              <h2 class="profile-page__card-title">Информация о компании</h2>
              <UiBadge v-if="company?.isVerified" variant="accent">Верифицирована</UiBadge>
              <UiBadge v-else-if="company" variant="neutral">На проверке</UiBadge>
              <UiBadge v-else variant="neutral">Не заполнено</UiBadge>
            </div>
            <div v-if="company || showAddCompanyForm" class="profile-page__card-body">
              <div class="profile-page__field">
                <label class="profile-page__label">Название компании</label>
                <UiInput
                  v-model="companyForm.name"
                  placeholder="Название"
                  :readonly="!editMode && !showAddCompanyForm"
                />
              </div>
              <div class="profile-page__field">
                <label class="profile-page__label">Полное наименование</label>
                <UiInput
                  v-model="companyForm.fullName"
                  placeholder="Полное наименование"
                  :readonly="!editMode && !showAddCompanyForm"
                />
              </div>
              <div class="profile-page__fields profile-page__fields--two">
                <div class="profile-page__field">
                  <label class="profile-page__label">ИНН</label>
                  <UiInput
                    v-model="companyForm.inn"
                    input-filter="digits"
                    maxlength="12"
                    placeholder="ИНН"
                    :readonly="!editMode && !showAddCompanyForm"
                  />
                </div>
                <div class="profile-page__field">
                  <label class="profile-page__label">КПП</label>
                  <UiInput
                    v-model="companyForm.kpp"
                    input-filter="digits"
                    maxlength="9"
                    placeholder="КПП"
                    :readonly="!editMode && !showAddCompanyForm"
                  />
                </div>
              </div>
              <div class="profile-page__field">
                <label class="profile-page__label">ОГРН</label>
                <UiInput
                  v-model="companyForm.ogrn"
                  input-filter="digits"
                  maxlength="15"
                  placeholder="ОГРН"
                  :readonly="!editMode && !showAddCompanyForm"
                />
              </div>
              <div class="profile-page__field">
                <label class="profile-page__label">Юридический адрес</label>
                <UiTextarea
                  v-model="companyForm.legalAddress"
                  :rows="2"
                  placeholder="Адрес"
                  :readonly="!editMode && !showAddCompanyForm"
                />
              </div>
              <div class="profile-page__field">
                <label class="profile-page__label">Фактический адрес</label>
                <UiTextarea
                  v-model="companyForm.actualAddress"
                  :rows="2"
                  placeholder="Адрес"
                  :readonly="!editMode && !showAddCompanyForm"
                />
              </div>
              <div class="profile-page__fields profile-page__fields--two">
                <div class="profile-page__field">
                  <label class="profile-page__label">Руководитель</label>
                  <UiInput
                    v-model="companyForm.director"
                    placeholder="ФИО"
                    :readonly="!editMode && !showAddCompanyForm"
                  />
                </div>
                <div class="profile-page__field">
                  <label class="profile-page__label">Телефон компании</label>
                  <UiInput
                    v-model="companyForm.phone"
                    input-filter="phone"
                    placeholder="+7 …"
                    aria-label="Телефон компании"
                    :readonly="!editMode && !showAddCompanyForm"
                  />
                </div>
              </div>
              <div class="profile-page__field">
                <label class="profile-page__label">Email компании</label>
                <UiInput
                  v-model="companyForm.email"
                  type="email"
                  placeholder="email@company.ru"
                  :readonly="!editMode && !showAddCompanyForm"
                />
              </div>
              <div
                v-if="showAddCompanyForm"
                class="profile-page__actions profile-page__actions--inline"
              >
                <UiButton variant="secondary" @click="cancelAddCompany">Отменить</UiButton>
                <UiButton :loading="saving" :disabled="!isCompanyFormValid" @click="createCompany"
                  >Создать компанию</UiButton
                >
              </div>
            </div>
            <div v-else class="profile-page__empty">
              <span class="profile-page__empty-icon">🏢</span>
              <h3 class="profile-page__empty-title">Информация о компании не заполнена</h3>
              <p class="profile-page__empty-text">
                Добавьте информацию о вашей компании для получения доступа к специальным условиям
              </p>
              <UiButton @click="showAddCompanyForm = true">Добавить компанию</UiButton>
            </div>
          </UiCard>
        </div>
      </div>

      <div v-if="editMode" class="profile-page__actions">
        <UiButton variant="secondary" @click="cancelEdit">Отменить</UiButton>
        <UiButton
          :loading="saving"
          :disabled="!isUserFormValid || (!!company && !isCompanyFormValid)"
          @click="saveProfile"
        >
          Сохранить изменения
        </UiButton>
      </div>
    </div>

    <UiModal
      v-model="showPasswordDialog"
      title="Изменение пароля"
      :max-width="500"
      @update:model-value="(v) => (showPasswordDialog = v)"
    >
      <div class="profile-page__modal-fields">
        <div class="profile-page__field">
          <label class="profile-page__label">Текущий пароль</label>
          <UiInput v-model="passwordForm.currentPassword" type="password" placeholder="••••••••" />
        </div>
        <div class="profile-page__field">
          <label class="profile-page__label">Новый пароль</label>
          <UiInput v-model="passwordForm.newPassword" type="password" placeholder="••••••••" />
        </div>
        <div class="profile-page__field">
          <label class="profile-page__label">Подтвердите пароль</label>
          <UiInput v-model="passwordForm.confirmPassword" type="password" placeholder="••••••••" />
        </div>
      </div>
      <template #footer>
        <UiButton variant="secondary" @click="showPasswordDialog = false">Отменить</UiButton>
        <UiButton
          :loading="changingPassword"
          :disabled="!isPasswordFormValid"
          @click="changePassword"
        >
          Изменить пароль
        </UiButton>
      </template>
    </UiModal>
  </UiContainer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthProfile } from '@/modules/auth/public'
import { useAppNotifications } from '@/modules/common/public'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiInput from '@/shared/ui/UiInput.vue'
import UiModal from '@/shared/ui/UiModal.vue'
import UiProgress from '@/shared/ui/UiProgress.vue'
import UiSwitch from '@/shared/ui/UiSwitch.vue'
import UiTextarea from '@/shared/ui/UiTextarea.vue'

const authProfile = useAuthProfile()
const notificationStore = useAppNotifications()

const loading = ref(false)
const saving = ref(false)
const changingPassword = ref(false)
const togglingTwoFactor = ref(false)
const editMode = ref(false)
const showPasswordDialog = ref(false)
const showAddCompanyForm = ref(false)
const twoFactorEnabled = ref(false)

const userForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
})

const companyForm = ref({
  name: '',
  fullName: '',
  inn: '',
  kpp: '',
  ogrn: '',
  legalAddress: '',
  actualAddress: '',
  director: '',
  phone: '',
  email: '',
})

const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
})

const user = computed(() => authProfile.user.value)
const company = computed(() => authProfile.company.value)

const rules = {
  required: (v: string) => !!v,
  email: (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
  phone: (v: string) => !v || /^[\+]?[0-9\s\-\(\)]{10,}$/.test(v),
  inn: (v: string) => !v || /^\d{10}$|^\d{12}$/.test(v),
  password: (v: string) => v.length >= 8,
}
const isUserFormValid = computed(() => {
  const u = userForm.value
  return (
    rules.required(u.firstName) &&
    rules.required(u.lastName) &&
    rules.required(u.email) &&
    rules.email(u.email) &&
    rules.phone(u.phone)
  )
})
const isCompanyFormValid = computed(() => {
  const c = companyForm.value
  return rules.required(c.name) && rules.required(c.inn) && rules.inn(c.inn) && rules.email(c.email)
})
const isPasswordFormValid = computed(() => {
  const p = passwordForm.value
  return (
    rules.required(p.currentPassword) &&
    rules.required(p.newPassword) &&
    rules.password(p.newPassword) &&
    p.newPassword === p.confirmPassword
  )
})

function formatDate(date: string | null): string {
  if (!date) return 'Не указано'
  return new Intl.DateTimeFormat('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date))
}

async function loadProfile() {
  loading.value = true
  try {
    await authProfile.fetchProfile()
    if (user.value) {
      userForm.value = {
        firstName: user.value.firstName ?? '',
        lastName: user.value.lastName ?? '',
        email: user.value.email ?? '',
        phone: user.value.phone ?? '',
      }
      twoFactorEnabled.value = user.value.twoFactorEnabled ?? false
    }
    if (company.value) {
      companyForm.value = {
        name: company.value.name ?? '',
        fullName: company.value.fullName ?? '',
        inn: company.value.inn ?? '',
        kpp: company.value.kpp ?? '',
        ogrn: company.value.ogrn ?? '',
        legalAddress: company.value.legalAddress ?? '',
        actualAddress: company.value.actualAddress ?? '',
        director: company.value.director ?? '',
        phone: company.value.phone ?? '',
        email: company.value.email ?? '',
      }
    }
  } catch (e) {
    console.error('Ошибка загрузки профиля', e)
    notificationStore.showError('Ошибка загрузки профиля')
  } finally {
    loading.value = false
  }
}

async function saveProfile() {
  if (!isUserFormValid.value || (company.value && !isCompanyFormValid.value)) return
  saving.value = true
  try {
    await authProfile.updateProfile(userForm.value)
    if (company.value) await authProfile.updateCompany(companyForm.value)
    editMode.value = false
    notificationStore.showSuccess('Профиль успешно обновлен')
  } catch (e) {
    console.error('Ошибка сохранения профиля', e)
    notificationStore.showError('Ошибка сохранения профиля')
  } finally {
    saving.value = false
  }
}

function cancelEdit() {
  editMode.value = false
  loadProfile()
}

function cancelAddCompany() {
  showAddCompanyForm.value = false
}

async function changePassword() {
  if (!isPasswordFormValid.value) return
  changingPassword.value = true
  try {
    await authProfile.changePassword({
      currentPassword: passwordForm.value.currentPassword,
      newPassword: passwordForm.value.newPassword,
    })
    showPasswordDialog.value = false
    passwordForm.value = { currentPassword: '', newPassword: '', confirmPassword: '' }
    notificationStore.showSuccess('Пароль успешно изменен')
  } catch (e) {
    console.error('Ошибка смены пароля', e)
    notificationStore.showError('Ошибка смены пароля')
  } finally {
    changingPassword.value = false
  }
}

async function createCompany() {
  saving.value = true
  try {
    await authProfile.createCompany(companyForm.value)
    showAddCompanyForm.value = false
    notificationStore.showSuccess('Компания успешно создана')
    await loadProfile()
  } catch (e) {
    console.error('Ошибка создания компании', e)
    notificationStore.showError('Ошибка создания компании')
  } finally {
    saving.value = false
  }
}

async function toggleTwoFactor(value: boolean) {
  togglingTwoFactor.value = true
  try {
    await authProfile.toggleTwoFactor(value)
    twoFactorEnabled.value = value
    notificationStore.showSuccess(
      value ? 'Подтверждение входа по email включено' : 'Подтверждение входа по email отключено',
    )
  } catch (e) {
    console.error('Ошибка обновления 2FA', e)
    twoFactorEnabled.value = user.value?.twoFactorEnabled ?? false
    notificationStore.showError('Ошибка обновления настроек безопасности')
  } finally {
    togglingTwoFactor.value = false
  }
}

onMounted(() => loadProfile())
</script>

<style scoped>
.profile-page {
  padding-top: 24px;
  padding-bottom: 48px;
}

.profile-page__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px;
  gap: 16px;
  color: #586578;
}

.profile-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.profile-page__title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
}

.profile-page__subtitle {
  margin: 0;
  color: #586578;
}

.profile-page__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

@media (max-width: 960px) {
  .profile-page__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.profile-page__col {
  min-width: 0;
}

.profile-page__card {
  margin-bottom: 24px;
  padding: 16px;
}

.profile-page__card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.profile-page__card-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
}

.profile-page__card-head .profile-page__card-title {
  margin: 0;
}

.profile-page__card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.profile-page__fields {
  display: grid;
  gap: 16px;
}

.profile-page__fields--two {
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 600px) {
  .profile-page__fields--two {
    grid-template-columns: minmax(0, 1fr);
  }
}

.profile-page__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.profile-page__label {
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.profile-page__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}

.profile-page__row:last-child {
  border-bottom: none;
}

.profile-page__row-title {
  display: block;
  font-weight: 500;
}

.profile-page__row-sub {
  font-size: 13px;
  color: #586578;
}

.profile-page__empty {
  text-align: center;
  padding: 32px 16px;
}

.profile-page__empty-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
  opacity: 0.6;
}

.profile-page__empty-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
}

.profile-page__empty-text {
  margin: 0 0 24px;
  font-size: 14px;
  color: #586578;
}

.profile-page__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.profile-page__modal-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
